import * as React from "react";
import * as ReactDOM from "react-dom";
import { Wifi, WifiOff, Clock, Loader2, HelpCircle, Play, type LucideIcon } from "lucide-react";
import * as s from "./DeviceHealthIndicator.style.css";
import type { DeviceHealthIndicatorProps, DeviceHealthStatus, DeviceHealthSize } from "./DeviceHealthIndicator.types";
import { deriveDeviceHealthStatus, INIT_THRESHOLD_MS, SERVICE_INIT_WINDOW } from "./resolveLastSignalAt";

export type { DeviceHealthIndicatorProps, DeviceHealthStatus, DeviceHealthSize } from "./DeviceHealthIndicator.types";
export { deriveDeviceHealthStatus } from "./resolveLastSignalAt";

// ─── Display maps ──────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<DeviceHealthStatus, string> = {
  online:           "Online",
  stale:            "Stale",
  offline:          "Offline",
  initializing:     "Initializing",
  unknown:          "Unknown",
  simulationActive: "Simulation Active",
  simulationPaused: "Simulation Paused",
};

function getInitializingDescription(serviceStartedAt?: number, productCreatedAt?: string | null): string {
  if (serviceStartedAt && Date.now() - serviceStartedAt < SERVICE_INIT_WINDOW) {
    return "Service recently restarted — connection status will update as devices report.";
  }
  if (productCreatedAt) {
    const ageMs = Date.now() - new Date(productCreatedAt).getTime();
    if (ageMs < INIT_THRESHOLD_MS) return "Waiting for the first device report.";
  }
  return "Waiting for the first device report.";
}

const STATUS_DESCRIPTION: Record<DeviceHealthStatus, string> = {
  online:           "Device is connected and reporting actively.",
  stale:            "Device has not reported recently.",
  offline:          "Device appears offline — no recent data.",
  initializing:     "Waiting for the first device report.",
  unknown:          "Connection status unavailable.",
  simulationActive: "Simulation is running and generating live telemetry.",
  simulationPaused: "Simulation is paused — no live data available.",
};

const ICON_SIZE: Record<DeviceHealthSize, number> = {
  sm: 12,
  md: 14,
  lg: 16,
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatRelativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ageMs = Date.now() - new Date(iso).getTime();
  if (ageMs < 0)          return "just now";
  if (ageMs < 60_000)     return "just now";
  if (ageMs < 3_600_000)  return `${Math.floor(ageMs / 60_000)} min ago`;
  if (ageMs < 86_400_000) return `${Math.floor(ageMs / 3_600_000)} hr ago`;
  return `${Math.floor(ageMs / 86_400_000)}d ago`;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const sec = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

// ─── Simulation timing hook ────────────────────────────────────────────────────

function useSimulationTimes(
  simulationStartedAt: string | null | undefined,
  simulationExpAt: string | null | undefined,
): { elapsed: string | null; remaining: string | null } {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!simulationStartedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [simulationStartedAt]);

  if (!simulationStartedAt) return { elapsed: null, remaining: null };
  const elapsedMs = now - new Date(simulationStartedAt).getTime();
  const elapsed = elapsedMs > 0 ? formatDuration(elapsedMs) : null;

  let remaining: string | null = null;
  if (simulationExpAt) {
    const remainingMs = new Date(simulationExpAt).getTime() - now;
    remaining = remainingMs > 0 ? formatDuration(remainingMs) : "Expiring…";
  }

  return { elapsed, remaining };
}

// ─── Component ─────────────────────────────────────────────────────────────────

export const DeviceHealthIndicator = ({
  lastSignalAt,
  lastTelemetryAt,
  lastShadowAt,
  productCreatedAt,
  serviceStartedAt,
  isSimulated,
  isSimulating,
  simulationExpAt,
  simulationStartedAt,
  size = "md",
  showLabel = false,
}: DeviceHealthIndicatorProps) => {
  // Tick every 60 s so derived status and relative-time tooltip stay current
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState<{ bottom: number; left: number } | null>(null);

  const showTooltip = React.useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left + rect.width / 2,
      });
    }
    setTooltipVisible(true);
  }, []);

  const hideTooltip = React.useCallback(() => setTooltipVisible(false), []);

  const status = deriveDeviceHealthStatus(lastSignalAt, productCreatedAt, serviceStartedAt, isSimulated, isSimulating);
  const iconSize = ICON_SIZE[size];
  const relTelemetry = formatRelativeTime(lastTelemetryAt);
  const relShadow = formatRelativeTime(lastShadowAt);

  const { elapsed: simElapsed, remaining: simRemaining } = useSimulationTimes(
    status === 'simulationActive' ? simulationStartedAt : null,
    status === 'simulationActive' ? simulationExpAt : null,
  );

  // ── Icon ────────────────────────────────────────────────────────────────────
  // simulationPaused uses an inline SVG so we can control bar width + gap precisely
  // at the small sizes used here — lucide's Pause uses filled rects that merge at 12–14px.
  const PauseSvg = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="5" y="4" width="4" height="16" rx="1" />
      <rect x="15" y="4" width="4" height="16" rx="1" />
    </svg>
  );

  let Icon: LucideIcon | null = null;
  let iconClass: string | undefined;
  let customIcon: React.ReactNode = null;

  if (status === 'simulationActive') {
    Icon = Play;
    iconClass = s.iconScalePulse;
  } else if (status === 'simulationPaused') {
    customIcon = <PauseSvg size={iconSize} />;
  } else if (status === 'online') {
    Icon = Wifi;
  } else if (status === 'stale') {
    Icon = Clock;
  } else if (status === 'offline') {
    Icon = WifiOff;
  } else if (status === 'initializing') {
    Icon = Loader2;
    iconClass = s.iconSpin;
  } else {
    Icon = HelpCircle;
    iconClass = s.iconPulse;
  }

  // ── Tooltip ─────────────────────────────────────────────────────────────────
  const tooltipEl = tooltipVisible && tooltipPos ? (
    <div
      className={s.tooltipPortal}
      style={{ position: "fixed", bottom: tooltipPos.bottom, left: tooltipPos.left, transform: "translateX(-50%)" }}
      role="tooltip"
    >
      <div className={s.tooltipInner} style={{ position: "relative" }}>
        <span className={s.tooltipTitle}>{STATUS_LABEL[status]}</span>

        {/* Simulation-specific timing rows */}
        {status === 'simulationActive' && simElapsed && (
          <span className={s.tooltipSignal}>Running for: {simElapsed}</span>
        )}
        {status === 'simulationActive' && simRemaining && (
          <span className={s.tooltipSignal}>Expires in: {simRemaining}</span>
        )}

        {/* Signal timestamps (shown for both simulated and non-simulated when available) */}
        {relTelemetry && (
          <span className={s.tooltipSignal}>Last telemetry: {relTelemetry}</span>
        )}
        {relShadow && (
          <span className={s.tooltipSignal}>Last shadow: {relShadow}</span>
        )}

        <span className={s.tooltipDescription}>
          {status === 'initializing'
            ? getInitializingDescription(serviceStartedAt, productCreatedAt)
            : STATUS_DESCRIPTION[status]}
        </span>
        <div className={s.tooltipCaret} />
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        ref={wrapperRef}
        className={s.wrapper}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <div className={s.iconContainer({ size, status })}>
          {customIcon ?? (Icon && (
            <Icon size={iconSize} strokeWidth={2.2} className={iconClass} />
          ))}
        </div>

        {showLabel && (
          <span className={s.label({ size, status })}>
            {STATUS_LABEL[status]}
          </span>
        )}
      </div>

      {typeof document !== "undefined" &&
        ReactDOM.createPortal(tooltipEl, document.body)}
    </>
  );
};
